import React, {FunctionComponent, useState} from "react";
import copy from "copy-to-clipboard";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import Icon from "./icon/Icon";
import IconTooltip from "./icon/IconTooltip";
import {TranslationLibFile, TranslationLibKey} from "../../utils/locale";
import {Action} from "../../utils/Action";

export enum AlertSeverity {
    DANGER = "alert--danger",
    INFO = "alert--info",
    SUCCESS = "alert--success",
    WARNING = "alert--warning"
}

export enum AlertStyle {
    FLAT = "alert--flat"
}

export enum AlertSize {
    BIG = "alert--big"
}

export type AlertItem = {
    text: string;
    title?: string;
    severity?: AlertSeverity;
    style?: AlertStyle;
    size?: AlertSize;
    fullWidth?: boolean;
    onClose?: () => void;
    isCopiable?: boolean;
    cssClass?: string;
}

const Alert: FunctionComponent<AlertItem> = (props) => {
    const {
        text,
        title,
        severity = AlertSeverity.INFO,
        style,
        size,
        fullWidth,
        onClose,
        isCopiable,
        cssClass
    } = props;

    const {t} = useTranslation(TranslationLibFile.COMMON);
    const [isCopied, setCopied] = useState(false);

    const onCopy = () => {
        const tag = document.createElement("div");
        tag.innerHTML = text;
        copy(tag.innerText);
        setCopied(true);
        setTimeout(() => setCopied(false), 500);
    };

    const buildActions = () => {
        if (!onClose && !isCopiable) {
            return null;
        }

        return (
            <div className="alert__actions">
                {isCopiable && (isCopied
                    ? <IconTooltip icon={Action.COPIED.icon} text={t(Action.COPIED.labelKey)} onClick={() => {}}/>
                    : <IconTooltip icon={Action.COPY.icon} text={t(Action.COPY.labelKey)} onClick={onCopy}/>
                )}
                {onClose && <IconTooltip icon={Action.CLOSE.icon} text={t(Action.CLOSE.labelKey)} onClick={onClose}/>}
            </div>
        );
    };

    let icon;
    switch (severity) {
        case AlertSeverity.DANGER:
            icon = <Icon name="report" outlined/>;
            break;
        case AlertSeverity.INFO:
            icon = <Icon name="lightbulb" outlined/>;
            break;
        case AlertSeverity.SUCCESS:
            icon = <Icon name="task_alt" outlined/>;
            break;
        case AlertSeverity.WARNING:
            icon = <Icon name="feedback" outlined/>;
            break;
        default:
            break;
    }

    return (
        <div className={clsx("alert", style, severity, size, {"alert--full-width": fullWidth}, cssClass)}>
            <div className="alert__content">
                {icon}
                <div className="alert__content__items">
                    {title && <span className="alert__content__items__title">{title}</span>}
                    <span dangerouslySetInnerHTML={{__html: text}}/>
                </div>
            </div>
            {buildActions()}
        </div>
    );
};

export default Alert;
